import { ORDER_LIST_FILTERS } from './orders-list-filters'
import { VTP_RETURNING_STATUS } from './orders-list-filter-options'
import type { OrderListValues } from './orders-list-filters'
import { getAnalyticsActor, posthog } from '@/lib/posthog'
import { MULTI_CHOICE_ALL } from '@/components/common/filters/multi-choice-filter'

/**
 * Nơi người dùng đổi bộ lọc: panel trái, sheet mobile, gỡ chip, "Xoá lọc",
 * nút "Xem toàn thời gian" khi danh sách tháng này rỗng, lối "Tìm toàn thời
 * gian" trong lúc tìm kiếm. (`einvoice_pending_notice` của dòng nhắc M-161 đã
 * bỏ cùng dòng nhắc ở M-166; event cũ vẫn còn trong PostHog.)
 */
export type OrderListFilterSurface =
    'sidebar' | 'sheet' | 'chip' | 'clear' | 'empty_state' | 'search_widen'

type FilterKey = keyof OrderListValues

// Ô tìm kiếm đã có event riêng (order_list_result_opened); gõ phím không phải
// thao tác lọc.
const TRACKED_KEYS = (
    Object.keys(ORDER_LIST_FILTERS.fields) as FilterKey[]
).filter((key) => key !== 'q')

const statusMode = (status: string) =>
    status === ORDER_LIST_FILTERS.defaults.status
        ? 'default'
        : status === MULTI_CHOICE_ALL
          ? 'all'
          : 'custom'

/**
 * Ô tick "VTP đang hoàn về" (M-86): `only` = chỉ xem đơn VTP đang hoàn về —
 * đúng ca ô này sinh ra để phục vụ; `included` = có trong tập đang xem;
 * `excluded` = đã bỏ tick.
 */
const vtpReturningMode = (status: string) => {
    if (status === MULTI_CHOICE_ALL) return 'included'
    const picked = status.split(',')
    if (!picked.includes(VTP_RETURNING_STATUS)) return 'excluded'
    return picked.length === 1 ? 'only' : 'included'
}

/**
 * Nhân viên dùng bộ lọc nào trên `/orders`. Chỉ gửi TÊN bộ lọc và giá trị
 * enum (khoảng thời gian, số trạng thái, tổ hợp thanh toán) — không ngày cụ
 * thể, không id hãng vận chuyển, không chuỗi tìm kiếm.
 */
export function captureOrderListFiltersChanged(input: {
    before: OrderListValues
    after: OrderListValues
    surface: OrderListFilterSurface
}) {
    try {
        const actor = getAnalyticsActor()
        if (!actor) return
        const { before } = input
        // Ghi đúng giá trị màn áp dụng sau khi qua URL: "Tùy chỉnh" hết ngày
        // là Tháng này, chọn đủ trạng thái là all.
        const after = ORDER_LIST_FILTERS.readValues(
            ORDER_LIST_FILTERS.toSearch(input.after),
        )
        const changed = TRACKED_KEYS.filter((key) => before[key] !== after[key])
        if (changed.length === 0) return
        posthog.capture('order_list_filters_changed', {
            schema_version: 1,
            event_id: crypto.randomUUID(),
            ...actor,
            route_template: '/orders',
            surface: input.surface,
            changed_keys: changed,
            active_keys: TRACKED_KEYS.filter(
                (key) => after[key] !== ORDER_LIST_FILTERS.defaults[key],
            ),
            period: after.period,
            status_mode: statusMode(after.status),
            status_count:
                after.status === MULTI_CHOICE_ALL
                    ? null
                    : after.status.split(',').length,
            payment_status: after.payment_status,
            vtp_returning: vtpReturningMode(after.status),
            // Có đang tìm không — không gửi chuỗi tìm. Tách "tìm không ra
            // trong kỳ rồi mở rộng" khỏi mở rộng khi chỉ xem danh sách.
            search_active: after.q !== '',
        })
    } catch {
        // Analytics must never block filtering.
    }
}

/**
 * Nút 3 ngạch ngang thu gọn / mở lại cột lọc desktop (M-59). Câu hỏi: có ai
 * dùng thật không, và họ thu gọn trong lúc danh sách ĐANG bị lọc hay không —
 * thu gọn khi còn bộ lọc bật là lúc dễ nhìn nhầm danh sách nhất.
 *
 * Chỉ gửi TÊN bộ lọc đang bật, không gửi giá trị, không gửi chuỗi tìm kiếm.
 */
export function captureOrderListSidebarToggled(input: {
    values: OrderListValues
    collapsed: boolean
}) {
    try {
        const actor = getAnalyticsActor()
        if (!actor) return
        const activeKeys = TRACKED_KEYS.filter(
            (key) => input.values[key] !== ORDER_LIST_FILTERS.defaults[key],
        )
        posthog.capture('order_list_filter_sidebar_toggled', {
            schema_version: 1,
            event_id: crypto.randomUUID(),
            ...actor,
            route_template: '/orders',
            collapsed: input.collapsed,
            active_keys: activeKeys,
            active_filter_count: activeKeys.length,
            search_active: input.values.q !== '',
        })
    } catch {
        // Analytics must never block the toggle.
    }
}
